import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  Input,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { SignupService } from 'src/app/services/signup/signup.service';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dashboard-approval-representatives',
  templateUrl: './dashboard-approval-representatives.component.html',
  styleUrls: ['./dashboard-approval-representatives.component.scss'],
})
export class DashboardApprovalRepresentativesComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() comparingVersions: boolean = false;
  private observer: MutationObserver;
  private handlersSet = false;

  @ViewChild('modalRoot') modalRoot;

  isContribuitor = false;

  representativeDetails = false;
  selectedRepresentative: Representative = null;

  data: Representative[] = [];

  // Dados para versão principal
  mainData: Representative[] = [];
  mainLoading = false;
  mainSigns: SignProps[] = [];

  // Dados para versão comparando
  comparingData: Representative[] = [];
  comparingLoading = false;
  comparingSigns: SignProps[] = [];

  eventMainVersion: any;
  eventComparingVersion: any;
  eventVersion: any;
  eventSignStatus: Subscription;

  companyLookupName: string | null = null;
  companyRepresentatives: Array<{
    name: string;
    document: string;
    canSign: boolean;
    isMandatory: boolean;
  }> = [];
  loadingCompanyRepresentatives = false;
  companyRepresentativesColumns: TableColumnList<{
    name: string;
    document: string;
    canSign: boolean;
    isMandatory: boolean;
  }>[] = [
    {
      name: 'Nome',
      cell: (row) => row.name,
    },
    {
      name: 'Documento',
      cell: (row) => this.formatCpfOrCnpj(row.document),
    },
    {
      name: 'Assina',
      cell: (row) => (row.canSign ? 'Sim' : 'Não'),
    },
    {
      name: 'Obrigatório',
      cell: (row) => (row.isMandatory ? 'Sim' : 'Não'),
    },
  ];

  pepColumns: TableColumnList<CompanyComplementaryPepListValues>[] = [
    {
      name: 'Nome',
      cell: (row) => `
    <span>${row.name}</span>
    <span class="app-table-details-body-row-subtitle">${row.document}</span>
  `,
      size: 2,
    },
    { name: 'Vínculo', cell: (row) => row.bound },
  ];

  pepData: CompanyComplementaryPepListValues[] = [
    {
      name: '0*',
      document: '0*',
      bound: '0*',
    },
    {
      name: '0*',
      document: '0*',
      bound: '0*',
    },
  ];

  showModal = false;
  file: Files = null;
  loading = true;

  isPDF = false;
  url: any;
  isXLS = false;

  cities_options: SelectItem[] = [];

  signs: SignProps[] = [];

  alreadyChecked = false;
  canView = false;
  role: string = '';
  comparingDataLoading = false;

  // Função auxiliar para formatar mandato
  formatMandate(minTerm: string | null, expirationDate: string | null): string {
    if (minTerm && expirationDate) {
      const minDate = new Date(minTerm).toLocaleDateString('pt-BR');
      const maxDate = new Date(expirationDate).toLocaleDateString('pt-BR');
      return `${minDate} - ${maxDate}`;
    } else if (minTerm) {
      const minDate = new Date(minTerm).toLocaleDateString('pt-BR');
      return `${minDate} - Indeterminado`;
    } else {
      return 'Indeterminado';
    }
  }

  getRepresentativeTypeText(item: any): string {
    const types: string[] = [];
    const isRepresentative = item.is_representative ?? item.representative;
    const isGuarantor = item.guarantor;
    const isSolidarityDebtor =
      item.solidarity_debtor ?? item.solidarityDebtor;

    if (isRepresentative) {
      types.push('Representante');
    }
    if (isGuarantor) {
      types.push('Avalista');
    }
    if (isSolidarityDebtor) {
      types.push('Devedor solidário');
    }

    return types.length > 0 ? types.join(' / ') : '--';
  }

  getMandatorySignature(item: any): boolean {
    return !!(
      item.is_registration_form_mandatory ??
      item.is_mandatory ??
      item.isMandatory
    );
  }

  getSignedStatus(item: any): boolean {
    return !!(
      item.has_signed ??
      item.signed ??
      item.is_signed
    );
  }

  isCompanyRepresentative(): boolean {
    return !!(
      (this.selectedRepresentative as any)?.company ||
      this.selectedRepresentative?.representative_register?.company
    );
  }

  isFundApproval(): boolean {
    const roleSlug =
      this.approvalService?.approvalData?.approval?.register?.role?.slug || '';
    return roleSlug.endsWith('-PJ-FI');
  }

  private getFundRoleFlag(
    item: Representative,
    role: 'is_adm' | 'is_manager'
  ): boolean {
    const companyData =
      (item as any)?.company ||
      item?.representative_register?.company ||
      (item as any)?.info?.company ||
      (item as any)?.info?.register?.company;
    const personData =
      item?.representative_register?.person ||
      (item as any)?.info?.register?.person;

    return !!(
      (item as any)?.[role] ??
      companyData?.[role] ??
      personData?.[role]
    );
  }

  getFundProfile(item: Representative): string {
    if (this.getFundRoleFlag(item, 'is_adm')) {
      return 'Administrador';
    }
    if (this.getFundRoleFlag(item, 'is_manager')) {
      return 'Gestor';
    }
    return 'Representante';
  }

  private buildSignStatusMap(): Map<number, any> {
    const signStatusList =
      this.approvalService.approvalData.representativeSignStatus || [];
    return new Map(signStatusList.map((status: any) => [status.id, status]));
  }

  private getSignStatus(signStatusMap: Map<number, any>, rep: any) {
    return (
      signStatusMap.get(rep.id) ||
      signStatusMap.get(rep.representative_id) ||
      signStatusMap.get(rep.representative?.id) ||
      null
    );
  }

  private applySignStatusToList(list: Representative[]): Representative[] {
    if (!list || list.length === 0) {
      return list;
    }

    const signStatusMap = this.buildSignStatusMap();
    if (signStatusMap.size === 0) {
      return list;
    }

    return list.map((item: any) => {
      const signStatus = this.getSignStatus(signStatusMap, item);
      if (!signStatus) {
        return item;
      }

      return {
        ...item,
        can_sign:
          signStatus.signs_registration_form ??
          (item.can_sign !== undefined ? item.can_sign : false),
        has_signed:
          signStatus.has_signed ??
          (item.has_signed !== undefined ? item.has_signed : false),
        is_registration_form_mandatory:
          signStatus.is_registration_form_mandatory ??
          item.is_registration_form_mandatory,
        signed:
          item.signed !== undefined
            ? signStatus.has_signed ?? item.signed
            : item.signed,
      };
    });
  }

  // Função para comparar os dados e aplicar classes de comparação
  async compareData() {
    try {
      this.comparingDataLoading = true;
      this.cdr.detectChanges();

      if (!this.mainData || !this.comparingData) {
        this.comparingDataLoading = false;
        this.comparingLoading = false;
        return;
      }

      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita)
      this.comparingData.forEach((comparingItem) => {
        const referenceItem = this.mainData.find(
          (ref) => ref.id === comparingItem.id
        );

        if (referenceItem) {
          // Formatar mandato para comparação
          const comparingMandate = this.formatMandate(
            comparingItem.min_term_validity,
            comparingItem.expiration_date || comparingItem.max_term_validity
          );
          const referenceMandate = this.formatMandate(
            referenceItem.min_term_validity,
            referenceItem.expiration_date || referenceItem.max_term_validity
          );

          // Item existe em ambas as tabelas - verificar se há diferenças
          const comparingSigned = this.getSignedStatus(comparingItem);
          const referenceSigned = this.getSignedStatus(referenceItem);
          const changedFields = {
            can_sign: comparingItem.can_sign !== referenceItem.can_sign,
            mandate: comparingMandate !== referenceMandate,
            is_representative:
              comparingItem.is_representative !==
              referenceItem.is_representative,
            guarantor: comparingItem.guarantor !== referenceItem.guarantor,
            solidarity_debtor:
              comparingItem.solidarity_debtor !==
              referenceItem.solidarity_debtor,
            signed: comparingSigned !== referenceSigned,
          };

          const hasChanges = Object.values(changedFields).some(
            (changed) => changed
          );

          // Adicionar campos formatados para exibição
          const formattedItem = {
            ...comparingItem,
            name: comparingItem.full_name,
            document:
              comparingItem.document_number || comparingItem.cpf_cnpj || '',
            signed: comparingSigned,
            mandate: comparingMandate,
            representative: comparingItem.is_representative,
            solidarityDebtor: comparingItem.solidarity_debtor,
            compareClass: hasChanges ? 'row-compare-modified' : '',
            changedFields: hasChanges
              ? {
                  signed: changedFields.can_sign,
                  mandate: changedFields.mandate,
                  representative: changedFields.is_representative,
                  guarantor: changedFields.guarantor,
                  solidarityDebtor: changedFields.solidarity_debtor,
                }
              : {},
          };

          processedData.push(formattedItem);
        } else {
          // Item novo (existe só na direita)
          const mandate = this.formatMandate(
            comparingItem.min_term_validity,
            comparingItem.expiration_date || comparingItem.max_term_validity
          );
          const comparingSigned = this.getSignedStatus(comparingItem);
          processedData.push({
            ...comparingItem,
            name: comparingItem.full_name,
            document:
              comparingItem.document_number || comparingItem.cpf_cnpj || '',
            signed: comparingSigned,
            mandate: mandate,
            representative: comparingItem.is_representative,
            solidarityDebtor: comparingItem.solidarity_debtor,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainData.forEach((referenceItem) => {
        const existsInComparing = this.comparingData.find(
          (comp) => comp.id === referenceItem.id
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed
          const mandate = this.formatMandate(
            referenceItem.min_term_validity,
            referenceItem.expiration_date || referenceItem.max_term_validity
          );
          const referenceSigned = this.getSignedStatus(referenceItem);
          processedData.push({
            ...referenceItem,
            name: referenceItem.full_name,
            document:
              referenceItem.document_number || referenceItem.cpf_cnpj || '',
            signed: referenceSigned,
            mandate: mandate,
            representative: referenceItem.is_representative,
            solidarityDebtor: referenceItem.solidarity_debtor,
            compareClass: 'row-compare-removed',
            changedFields: {},
          });
        }
      });

      this.comparingData = processedData;
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    } catch (error) {
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    }
  }

  constructor(
    private signupService: SignupService,
    private maskPipe: MaskPipe,
    private api: ApiService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private authService: AuthService,
    private el: ElementRef,
    private router: Router,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange
    ) {
      const previousValue = changes['comparingVersions'].previousValue;
      const currentValue = changes['comparingVersions'].currentValue;

      // Se mudou de true para false, recarregar dados
      if (previousValue === true && currentValue === false) {
        if (this.approvalService.approvalData.representative) {
          this.signs = this.approvalService.approvalData.signs;
          this.data = this.approvalService.approvalData.representative;
          this.loading = false;
        } else {
          this.getApprovalData();
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.eventVersion) {
      this.eventVersion.unsubscribe();
    }
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
    if (this.eventSignStatus) {
      this.eventSignStatus.unsubscribe();
    }
  }

  ngOnInit(): void {
    let range = this.router.url.split('/').length;
    this.role = this.router.url.split('/')[range - 2];

    this.canView = this.authService.verifyPermission(
      `de_can_view_${
        this.router.url.split('/')[range - 2]
      }_representatives_details`
    );

    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.representative) {
        this.signs = this.approvalService.approvalData.signs;
        this.data = this.approvalService.approvalData.representative;

        this.loading = false;
        // Os handlers serão configurados no ngAfterViewInit
      } else {
        this.getApprovalData();
      }
    }

    // Listener para mudanças de versão
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Incrementar contador de loading v3 quando iniciar requisição
        this.approvalService.incrementV3Loading();

        // Ativar loading em todas as abas quando trocar de versão
        this.loading = true;
        this.getApprovalData();
      }
    });

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });

    this.eventSignStatus =
      this.approvalService.updateRepresentativeSignStatus.subscribe(() => {
        this.data = this.applySignStatusToList(this.data);
        this.mainData = this.applySignStatusToList(this.mainData);
        this.comparingData = this.applySignStatusToList(this.comparingData);
        if (this.comparingVersions) {
          this.compareData();
        } else {
          this.cdr.detectChanges();
        }
      });

    // MutationObserver para configurar handlers quando novos elementos forem adicionados
    this.observer = new MutationObserver((mutations) => {
      // Debounce para evitar múltiplas chamadas
      if (this.handlersSet) return;

      const item = this.el.nativeElement.querySelector(
        '.representative-details'
      );

      if (item) {
        this.handlersSet = true;
        setTimeout(() => {
          this.setHandlers();
          this.handlersSet = false;
        }, 100);
      }
    });

    this.observer.observe(this.el.nativeElement, {
      childList: true, // observar adições/remover elementos
      subtree: true, // observar subelementos
    });
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainRevision) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingRevision) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainRevision) {
      this.mainData = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingRevision) {
      this.comparingData = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.comparingDataLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('comparing');
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      // Obter register_uuid da rota
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainSigns = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingSigns = [];
          this.comparingLoading = false;
        } else {
          this.data = [];
          this.signs = [];
          this.loading = false;
        }
        this.cdr.detectChanges();
        return;
      }

      // Parâmetros da API v3
      // Se houver versão selecionada no approvalService, usar ela; caso contrário, usar last=true
      const params: any = {
        data_type: 'representatives',
      };

      // Verificar se há versão específica selecionada
      let versionToUse = null;
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        versionToUse = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        versionToUse = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        versionToUse = this.approvalService.approvalData.version;
      }

      if (versionToUse) {
        params.version = versionToUse;
      } else {
        params.last = true;
      }

      // Chamar API v3
      const representativesResponse = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: params,
        token: true,
      });

      // Processar resposta da API v3
      if (representativesResponse && representativesResponse.data) {
        const representativesData = this.processRepresentativesData(
          representativesResponse,
          register_uuid
        );

        if (versionType === 'main') {
          this.mainData = representativesData || [];
          this.mainSigns = []; // TODO: Processar signs se necessário
          this.mainLoading = false;
          // Se já temos comparing data, fazer comparação
          if (this.comparingVersions && this.comparingData) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          // Usar requestAnimationFrame para garantir que o DOM foi atualizado
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.setHandlers();
            });
          });
        } else if (versionType === 'comparing') {
          this.comparingData = representativesData || [];
          this.comparingSigns = []; // TODO: Processar signs se necessário
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          // Se já temos main data, fazer comparação
          if (this.comparingVersions && this.mainData) {
            await this.compareData();
          }
          this.cdr.detectChanges();
          // Usar requestAnimationFrame para garantir que o DOM foi atualizado
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.setHandlers();
            });
          });
        } else {
          this.data = representativesData || [];
          this.signs = []; // TODO: Processar signs se necessário
          this.loading = false;

          // Armazenar no cache apenas se não estiver em modo de comparação
          if (!this.comparingVersions && representativesData) {
            this.approvalService.approvalData.representative =
              representativesData;
            this.approvalService.approvalData.signs = this.signs;
          }

          this.cdr.detectChanges();
          // Usar requestAnimationFrame para garantir que o DOM foi atualizado
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.setHandlers();
            });
          });
        }
      } else {
        // Sem dados na resposta
        if (versionType === 'main') {
          this.mainData = [];
          this.mainSigns = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingSigns = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
        } else {
          this.data = [];
          this.signs = [];
          this.loading = false;
        }
        this.cdr.detectChanges();
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainData = [];
        this.mainSigns = [];
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingData = [];
        this.comparingSigns = [];
        this.comparingLoading = false;
        this.comparingDataLoading = false;
      } else {
        this.data = [];
        this.signs = [];
        this.loading = false;
      }
      this.cdr.detectChanges();
    } finally {
      // Decrementar contador de loading v3 quando terminar requisição normal (não comparação)
      if (!versionType && !this.comparingVersions) {
        this.approvalService.decrementV3Loading();
      }
    }
  }

  /**
   * Processa os dados de representantes da API v3
   * Estrutura da resposta: data.versions.representatives.versions[0].data (array)
   */
  private processRepresentativesData(
    response: any,
    register_uuid?: string
  ): Representative[] | null {
    // Extrair dados da estrutura da API v3
    const representativesArray =
      response?.data?.versions?.representatives?.versions?.[0]?.data;
    const registerType = response?.data?.register_type;

    if (!representativesArray || !Array.isArray(representativesArray)) {
      return null;
    }

    // Verificar se é cedente PF (assignor + PF)
    // Quando é cedente PF, todos os representantes sempre são representantes
    const isAssignorPF = this.role === 'assignor' && registerType === 'PF';

    if (isAssignorPF) {
    }

    // Mapear os dados para o formato esperado pelo componente
    // A estrutura da API v3 já está no formato correto, mas podemos fazer ajustes se necessário
    const signStatusMap = this.buildSignStatusMap();

    return representativesArray.map((rep: any) => {
      const signStatus = this.getSignStatus(signStatusMap, rep);
      const fallbackStatus = signStatusMap.get(rep.represented_register) || null;
      // Garantir que os campos esperados estejam presentes
      const mapped = {
        ...rep,
        // Mapear campos se necessário
        full_name: rep.full_name || rep.name || '',
        document_number: rep.document_number || rep.document || '',
        cpf_cnpj: rep.document_number || rep.document || '',
        can_sign:
          signStatus?.signs_registration_form ??
          fallbackStatus?.signs_registration_form ??
          (rep.can_sign !== undefined ? rep.can_sign : false),
        has_signed:
          signStatus?.has_signed ??
          fallbackStatus?.has_signed ??
          (rep.has_signed !== undefined ? rep.has_signed : false),
        is_registration_form_mandatory:
          signStatus?.is_registration_form_mandatory ??
          fallbackStatus?.is_registration_form_mandatory ??
          rep.is_registration_form_mandatory,
        // Se for cedente PF, sempre marcar como representante
        is_representative: isAssignorPF
          ? true
          : rep.is_representative !== undefined
          ? rep.is_representative
          : false,
        expiration_date: rep.expiration_date || rep.max_term_validity || null,
        min_term_validity: rep.min_term_validity || null,
        max_term_validity: rep.expiration_date || rep.max_term_validity || null,
      } as Representative;
      return mapped;
    });

    /* Código antigo comentado - não chamar APIs v2
    try {
      const req_params: any = {
        register: this.route.snapshot.params.id,
        tab: 'representatives',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        req_params['version'] = this.approvalService.approvalData.version;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainRevision) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingRevision))
      ) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }

      /* Código antigo comentado - não chamar APIs v2
      const data = await this.api.get<UserApprovalResponse[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      if (versionType === 'main') {
        this.mainData = data[0].register.representatives || [];
        this.mainSigns = data[0].register.company?.signs || [];
        this.mainLoading = false;
        // Se já temos comparing data, fazer comparação
        if (this.comparingVersions && this.comparingData) {
          await this.compareData();
        }
        this.cdr.detectChanges();
        // Usar requestAnimationFrame para garantir que o DOM foi atualizado
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.setHandlers();
          });
        });
      } else if (versionType === 'comparing') {
        this.comparingData = data[0].register.representatives || [];
        this.comparingSigns = data[0].register.company?.signs || [];
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        // Se já temos main data, fazer comparação
        if (this.comparingVersions && this.mainData) {
          await this.compareData();
        }
        this.cdr.detectChanges();
        // Usar requestAnimationFrame para garantir que o DOM foi atualizado
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.setHandlers();
          });
        });
      } else {
        this.approvalService.approvalData.representative =
          data[0].register.representatives;
        this.approvalService.approvalData.signs =
          data[0].register.company?.signs || [];

        this.data = data[0].register.representatives;
        this.signs = data[0].register.company?.signs || [];

        this.loading = false;
        this.cdr.detectChanges();
        // Usar requestAnimationFrame para garantir que o DOM foi atualizado
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.setHandlers();
          });
        });
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        this.cdr.detectChanges();
      } else {
        this.loading = false;
      }
    }
    */
  }

  setHandlers() {
    const representative = this.el.nativeElement.querySelectorAll(
      '.representative-details'
    );

    representative.forEach((button: any, index: number) => {
      // Verificar se já tem listener para evitar duplicatas
      if (button._hasListener) {
        return;
      }

      button._hasListener = true;

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const el: any = event.currentTarget;
        const idClass: any = el.classList.item(1);

        if (!idClass) {
          return;
        }

        const id = idClass.split('-')[1];

        if (!id) {
          return;
        }

        // Buscar em todos os dados (main, comparing ou normal)
        const allData = this.comparingVersions
          ? [...this.mainData, ...this.comparingData]
          : this.data;

        const finded = allData.find((item: any) => {
          if (item.id) {
            return Number(item.id) === Number(id);
          }
          return false;
        });

        if (finded) {
          this.openModal(finded);
        }
      });
    });
  }

  closeModal() {
    this.representativeDetails = false;
    this.companyLookupName = null;
    this.companyRepresentatives = [];
    this.loadingCompanyRepresentatives = false;
  }

  openModal(item: Representative) {
    try {
      this.selectedRepresentative = item;
      this.isContribuitor = this.selectedRepresentative?.representative_register
        ?.department
        ? true
        : false;
      this.representativeDetails = true;

      const companyRegister =
        (this.selectedRepresentative as any)?.company ||
        this.selectedRepresentative?.representative_register?.company;
      const companyRegisterUuid = companyRegister?.register_uuid;
      const companyName = companyRegister?.corporate_name;
      if (companyRegisterUuid) {
        this.companyLookupName = companyName || 'Empresa encontrada';
        this.loadCompanyRepresentatives(companyRegisterUuid);
      } else {
        this.companyLookupName = null;
        this.companyRepresentatives = [];
        this.loadingCompanyRepresentatives = false;
      }
    } catch (error) {
      this.representativeDetails = false;
    }
  }

  // Função para abrir modal pelo ID
  openModalById(id: number) {
    try {
      // Buscar em todos os dados disponíveis
      let allData: any[] = [];

      if (this.comparingVersions) {
        // Se estiver comparando versões, buscar em mainData e comparingData
        allData = [...this.mainData, ...this.comparingData];
      } else {
        // Se não estiver comparando, buscar em data
        allData = this.data;
      }

      const finded = allData.find((item: any) => {
        if (item.id) {
          return Number(item.id) === Number(id);
        }
        return false;
      });

      if (finded) {
        this.openModal(finded);
      }
    } catch (error) {
      // Erro silencioso - não fazer nada
    }
  }

  private async loadCompanyRepresentatives(registerUuid: string) {
    try {
      this.loadingCompanyRepresentatives = true;
      this.companyRepresentatives = [];

      const signStatusRes = await this.api.get<{
        data: Array<{
          id: number;
          full_name: string;
          document_number: string;
          signs_registration_form: boolean;
          is_registration_form_mandatory: boolean;
          has_signed: boolean;
        }>;
      }>({
        route: 'api/registration/registration-sign-status/',
        params: { register_uuid: registerUuid },
        token: true,
      });

      this.companyRepresentatives = (signStatusRes?.data || []).map((item) => ({
        name: item.full_name || '--',
        document: item.document_number || '--',
        canSign: item.signs_registration_form,
        isMandatory: item.is_registration_form_mandatory,
      }));
    } catch (error) {
      this.companyRepresentatives = [];
    } finally {
      this.loadingCompanyRepresentatives = false;
    }
  }

  getChoicesName(object: string, value: any) {
    const choices = this.signupService.signupChoices;

    const choiceObject = choices[object];

    if (choiceObject) {
      return choiceObject.find((_choice) => _choice.value == value);
    }

    return { label: '', value: '' };
  }

  getNacionality(value: any) {
    const choices = this.signupService.nationalityChocies;

    if (value) {
      return choices.find((_choice) => _choice.value == value);
    }

    return { label: '', value: '' };
  }

  getFullName(representative: any) {
    if (representative) {
      return `${representative.first_name} ${representative.last_name}`;
    }
    return '';
  }

  formatCpf(value: string) {
    const newValue = this.maskPipe.transform(value, '000.000.000-00');

    return newValue;
  }

  formatCnpj(value: string) {
    const newValue = this.maskPipe.transform(value, '00.000.000/0000-00');

    return newValue;
  }

  formatCpfOrCnpj(value: string) {
    if (!value) return '--';
    
    // Remove caracteres não numéricos
    const cleaned = value.replace(/\D/g, '');
    
    // Se tem 11 dígitos, é CPF
    if (cleaned.length === 11) {
      return this.formatCpf(cleaned);
    }
    
    // Se tem 14 dígitos, é CNPJ
    if (cleaned.length === 14) {
      return this.formatCnpj(cleaned);
    }
    
    // Se não for nem CPF nem CNPJ, retorna o valor original
    return value;
  }

  formatRg(value: string) {
    const newValue = this.maskPipe.transform(value, '00.000.000-0');

    return newValue;
  }

  formatDocument(document: any) {
    if (document.type === 'CPF') {
      return this.formatCpf(document.number);
    }

    if (document.type === 'RG') {
      return this.formatRg(document.number);
    }

    return document.number;
  }

  formatNumber(cellphone: any) {
    if (cellphone && !cellphone.number) return '--';

    const numberWithoutDDI = cellphone.number.slice(-11);
    const ddi = numberWithoutDDI.slice(0, 2);
    const onlyNumber = numberWithoutDDI.slice(2);
    const formatPhone = this.maskPipe.transform(onlyNumber, '00000-0000');

    const formatedPhone = `+${cellphone.code} (${ddi}) ${formatPhone}`;
    return formatedPhone;
  }

  formatDate(date?: string) {
    return date ? date.split('-').reverse().join('/') : '--';
  }

  formatBrl(amount: string) {
    const formatted = Utils.formatBrl(Number(amount));

    return formatted;
  }

  formatAddress(address: any) {
    const formatedAddress = `
    ${address.public_place}, ${address.number}, ${address.complement ?? ''}\n${
      address.district
    }, ${address.city} - ${address.uf}

    `;

    return formatedAddress;
  }

  handleSelect(idx: any) {
    this.file = this.selectedRepresentative.files[idx];
    this.getImgUrl();
  }

  getImgUrl() {
    const regex = /\.([0-9a-z]+)(?:[\?#]|$)/i;

    const resultado = regex.exec(this.file.file);

    if (resultado && resultado[1]) {
      const extension = resultado[1].toLowerCase();

      switch (extension) {
        case 'pdf':
          this.isPDF = true;
          break;
        case 'xls':
        case 'xlsx':
          this.isXLS = true;
          break;
        default:
          this.isPDF = false;
          this.isXLS = false;
          break;
      }
    }

    let url: any = this.file.file;

    if (this.isPDF) {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(this.file.file);
    }

    if (this.isXLS) {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://view.officeapps.live.com/op/embed.aspx?src=${this.file.file}`
      );
    }

    this.url = url;

    this.modalRoot.show();
  }

  ngAfterViewInit(): void {
    // Configurar handlers após a view inicializar
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setHandlers();
      });
    });
  }

  //   setHistoryData() {
  //     this.data =
  //       this.approval.history.Representative ||
  //       this.approval.register.representatives;
  //   }
}
